import React, { useMemo, useState } from 'react';
import {
  useSuspenseQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { atcQueryKeys } from '../query-keys';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { KillConfirmationDialog } from './kill-confirmation-dialog';

interface OrgDetailDrawerProps {
  orgId: string | null;
  orgName: string;
  activeCount: number;
  timeInterval: TimeInterval;
  onClose: () => void;
}

const DrawerContent: React.FC<{
  orgId: string;
  orgName: string;
  activeCount: number;
  timeInterval: TimeInterval;
  onClose: () => void;
}> = ({ orgId, orgName, activeCount, timeInterval, onClose }) => {
  const client = useUntenantedClient();
  const queryClient = useQueryClient();
  const [showKillDialog, setShowKillDialog] = useState(false);

  const { data: invocationData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getInvocationSummary({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.invocationSummary(timeInterval),
  });

  const orgInvocations = useMemo(
    () => invocationData.filter((row) => row.org === orgId),
    [invocationData, orgId]
  );

  const killMutation = useMutation({
    mutationFn: async () => {
      const res = await client.atcVisibility.killOrgInvocations({ orgId });
      return Assert.assertOk(res);
    },
    onSuccess: () => {
      setShowKillDialog(false);
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: atcQueryKeys.all,
          refetchType: 'all',
        });
      }, 1500);
      onClose();
    },
  });

  return (
    <>
      <SheetHeader>
        <SheetTitle>{orgName}</SheetTitle>
        <SheetDescription className="font-mono text-xs">
          {orgId}
        </SheetDescription>
        <div className="flex gap-2 pt-2">
          <Badge variant={activeCount > 0 ? 'amber' : 'green'}>
            {activeCount} active invocation{activeCount !== 1 ? 's' : ''}
          </Badge>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto px-4">
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700">
            Recent Invocations by Service
          </h4>
          {orgInvocations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No invocations found in this time range.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Success</TableHead>
                  <TableHead>Failure</TableHead>
                  <TableHead>Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgInvocations.map((inv) => (
                  <TableRow key={inv.service}>
                    <TableCell className="font-mono text-xs">
                      {inv.service}
                    </TableCell>
                    <TableCell className="text-green-600">
                      {inv.successCount}
                    </TableCell>
                    <TableCell
                      className={
                        inv.failureCount > 0 ? 'text-red-600 font-semibold' : ''
                      }
                    >
                      {inv.failureCount}
                    </TableCell>
                    <TableCell className="font-medium">{inv.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {activeCount > 0 && (
        <SheetFooter>
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setShowKillDialog(true)}
          >
            Kill All Invocations
          </Button>
        </SheetFooter>
      )}

      <KillConfirmationDialog
        open={showKillDialog}
        orgName={orgName}
        orgId={orgId}
        isPending={killMutation.isPending}
        onConfirm={() => killMutation.mutate()}
        onCancel={() => setShowKillDialog(false)}
      />
    </>
  );
};

export const OrgDetailDrawer: React.FC<OrgDetailDrawerProps> = ({
  orgId,
  orgName,
  activeCount,
  timeInterval,
  onClose,
}) => {
  return (
    <Sheet
      open={orgId !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <SheetContent
        side="right"
        className="w-[500px] sm:max-w-[500px] flex flex-col"
      >
        {orgId !== null && (
          <React.Suspense
            fallback={
              <div className="p-4 text-sm text-muted-foreground">
                Loading...
              </div>
            }
          >
            <DrawerContent
              orgId={orgId}
              orgName={orgName}
              activeCount={activeCount}
              timeInterval={timeInterval}
              onClose={onClose}
            />
          </React.Suspense>
        )}
      </SheetContent>
    </Sheet>
  );
};
